import { idiomaInicial, traduzir } from "@/lib/i18n";
import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";

const ACCESS = "gb_access";
const REFRESH = "gb_refresh";
const GARAGE = "gb_garage";

/** Encerra a sessão: apaga tokens E o cache de dados.
 *
 * ⚠ Limpar só o token não basta. A navegação entre /entrar e o painel é
 * do React Router — a página não recarrega, então o QueryClient mantém
 * o `["me"]` do usuário anterior (staleTime de 5 min). O próximo login
 * era servido pelo cache antigo: token do usuário B, tela do usuário A,
 * incluindo o financeiro da oficina dele.
 *
 * Vale para os dois sentidos da transição — sair e entrar.
 */
export function clearSession(queryClient?: { clear: () => void }) {
  tokens.clear();
  queryClient?.clear();
}

export const tokens = {
  get access() {
    return localStorage.getItem(ACCESS);
  },
  get refresh() {
    return localStorage.getItem(REFRESH);
  },
  get garage() {
    return localStorage.getItem(GARAGE);
  },
  set({ access, refresh }: { access: string; refresh?: string }) {
    localStorage.setItem(ACCESS, access);
    if (refresh) localStorage.setItem(REFRESH, refresh);
  },
  setGarage(slug: string) {
    localStorage.setItem(GARAGE, slug);
  },
  clear() {
    localStorage.removeItem(ACCESS);
    localStorage.removeItem(REFRESH);
    localStorage.removeItem(GARAGE);
  },
};

/** Base da API.
 *
 * Em produção o front é um site estático no S3 e o Django roda no VPS —
 * são origens diferentes, então um caminho relativo como "/api/v1" bateria
 * no próprio bucket e devolveria 404. Precisa ser URL absoluta.
 *
 * Em dev fica "/api/v1" e o proxy do Vite encaminha para localhost:8000,
 * mantendo tudo na mesma origem.
 */
export const API_BASE = import.meta.env.VITE_API_URL || "/api/v1";

export const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  if (tokens.access) config.headers.Authorization = `Bearer ${tokens.access}`;
  // Só envia o header quando o usuário tem mais de uma oficina; sem
  // ele o backend usa o único vínculo ativo.
  if (tokens.garage) config.headers["X-Garage"] = tokens.garage;
  return config;
});

/** Erro de negócio no formato { error: { code, message, field } }. */
export type ApiError = {
  code: string;
  message: string;
  field: string | null;
};

export function toApiError(err: unknown): ApiError {
  const payload = (err as AxiosError<{ error?: ApiError }> | null | undefined)?.response?.data
    ?.error;
  if (payload) return payload;
  return {
    code: "NETWORK_ERROR",
    // Fora de componente não há Provider, então o idioma vem da mesma
    // fonte das telas de login e cadastro: `?lang=`, o que já foi
    // escolhido neste host, e por fim o navegador.
    message: traduzir(idiomaInicial(), "error.offline"),
    field: null,
  };
}

// --- refresh automático de token -------------------------------------
// Uma única promessa de refresh compartilhada: se cinco requisições
// caírem em 401 ao mesmo tempo (o que acontece ao abrir a agenda), todas
// esperam o mesmo refresh em vez de dispararem cinco.
let refreshing: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  const refresh = tokens.refresh;
  if (!refresh) throw new Error("sem refresh token");
  // axios "cru" de propósito: passar pela instância `api` cairia no
  // interceptor de 401 e entraria em laço de refresh.
  const { data } = await axios.post(`${API_BASE}/auth/refresh/`, { refresh });
  tokens.set({ access: data.access });
  return data.access;
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & {
      _retried?: boolean;
    };

    const isAuthCall = original?.url?.includes("/auth/");
    if (error.response?.status !== 401 || original?._retried || isAuthCall) {
      return Promise.reject(error);
    }

    original._retried = true;
    try {
      refreshing ??= refreshAccessToken().finally(() => {
        refreshing = null;
      });
      const access = await refreshing;
      original.headers.Authorization = `Bearer ${access}`;
      return api(original);
    } catch {
      tokens.clear();
      window.location.href = "/entrar";
      return Promise.reject(error);
    }
  },
);
